import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LoadingScreen from '../components/LoadingScreen';
import supabase from '../supabase';
import { useSiteLang as useLang } from '../siteLang';

// Copy bilingue légère pour les nouvelles sections de la page d'accueil
// (mêmes clés que dans Chroniques.jsx / Campaign.jsx : fr / en / vi)
const HOME_T = {
    fr: {
        eyebrow: 'Chroniques',
        heading: 'Derniers récits archivés',
        viewAll: 'Voir toutes les chroniques →',
        viewCampaign: 'Voir la campagne →',
        empty: 'Aucun album pour le moment.',
        loading: 'Chargement…',
        campaign: (y) => `Campagne ${y}`,
        by: 'Photographie par',
    },
    en: {
        eyebrow: 'Chronicles',
        heading: 'Latest archived stories',
        viewAll: 'View all chronicles →',
        viewCampaign: 'View the campaign →',
        empty: 'No albums yet.',
        loading: 'Loading…',
        campaign: (y) => `Campaign ${y}`,
        by: 'Photography by',
    },
    vi: {
        eyebrow: 'Biên niên sử',
        heading: 'Những câu chuyện lưu trữ mới nhất',
        viewAll: 'Xem tất cả biên niên sử →',
        viewCampaign: 'Xem chiến dịch →',
        empty: 'Chưa có album nào.',
        loading: 'Đang tải…',
        campaign: (y) => `Chiến dịch ${y}`,
        by: 'Nhiếp ảnh bởi',
    },
};

function Home() {
    const navigate = useNavigate();
    const { lang } = useLang();
    const homeT = HOME_T[lang] || HOME_T.fr;

    const [popupOpen, setPopupOpen] = useState(false);
    const [popupData, setPopupData] = useState(null);
    const [announcement, setAnnouncement] = useState(null);
    const [campaign, setCampaign] = useState(null);
    const [campaignLoading, setCampaignLoading] = useState(true);

    const [recentAlbums, setRecentAlbums] = useState([]);
    const [albumsLoading, setAlbumsLoading] = useState(true);
    const color = '#f4efe4';
    const UI_FONT = 'Helvetica Neue, Arial, sans-serif';

    // Campagne active — celle affichée en background sur Home
    useEffect(() => {
        const fetchActiveCampaign = async () => {
            const { data, error } = await supabase.from('campaigns').select('*').eq('is_active', true).single();
            if (error) console.error('Erreur fetch campaign:', error.message);
            if (data) setCampaign(data);
            setCampaignLoading(false);
        };
        fetchActiveCampaign();
    }, []);

    // OG image = photo de campagne — dépend de campaign, se déclenche une fois chargée
    useEffect(() => {
        if (!campaign?.image_url) return;
        let tag = document.querySelector('meta[property="og:image"]');
        if (!tag) {
            tag = document.createElement('meta');
            tag.setAttribute('property', 'og:image');
            document.head.appendChild(tag);
        }
        const isAbsolute = campaign.image_url.startsWith('http');
        tag.setAttribute('content', isAbsolute ? campaign.image_url : `${window.location.origin}${campaign.image_url}`);
    }, [campaign]);

    useEffect(() => {
        const fetchPopup = async () => {
            const { data } = await supabase.from('popup').select('*').single();
            if (data && data.enabled) { setPopupData(data); setPopupOpen(true); }
        };
        fetchPopup();
    }, []);

    useEffect(() => {
        const fetchAnnouncement = async () => {
            const { data } = await supabase
                .from('announcements')
                .select('*')
                .eq('enabled', true)
                .order('date', { ascending: false })
                .limit(1)
                .maybeSingle();
            if (data) setAnnouncement(data);
        };
        fetchAnnouncement();
    }, []);

    // Les 3 derniers récits archivés — nourrit la section "Chroniques" sous le hero.
    // Indépendant du chargement de la campagne : ne bloque pas l'affichage du hero.
    useEffect(() => {
        const fetchRecent = async () => {
            const { data, error } = await supabase
                .from('albums')
                .select('*')
                .eq('archived', false)
                .order('date', { ascending: false })
                .limit(3);
            if (!error && data) setRecentAlbums(data);
            setAlbumsLoading(false);
        };
        fetchRecent();
    }, []);

    const typeLabels = {
        release: '— Diffusion immédiate —',
        media: '— Communiqué de presse —',
        global: '— Global release now —',
        update: '— Update —',
    };

    const formatAnnouncementDate = (a) => {
        const d = a?.date ? new Date(`${a.date}T00:00:00`) : a?.created_at ? new Date(a.created_at) : new Date();
        return d.toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    // Section "Chroniques" — réutilisée que la campagne active existe ou non,
    // pour que la page ne reste jamais totalement vide.
    const chroniclesSection = (
        <div style={{ background: '#0e1013', padding: 'clamp(72px, 9vw, 110px) 0 clamp(80px, 9vw, 120px)', width: '100%', boxSizing: 'border-box' }}>
            <style>{`
                .home-chronicle-card { transition: transform .3s ease; cursor: pointer; }
                .home-chronicle-card:hover { transform: translateY(-4px); }
                .home-chronicle-card .home-chronicle-cover img { transition: transform .5s ease; }
                .home-chronicle-card:hover .home-chronicle-cover img { transform: scale(1.03); }
            `}</style>
            <div style={{ width: '100%', boxSizing: 'border-box', padding: '0 clamp(24px, 4.5vw, 64px)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '24px' }}>
                    <div>
                        <p style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontWeight: 700, fontSize: '10px', letterSpacing: '0.24em', textTransform: 'uppercase', color: 'rgba(244,239,228,0.45)', margin: '0 0 14px' }}>
                            {homeT.eyebrow}
                        </p>
                        <h2 style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontWeight: 700, fontSize: 'clamp(26px, 3.5vw, 42px)', color: '#f4efe4', margin: 0, letterSpacing: '-0.01em', maxWidth: '640px' }}>
                            {homeT.heading}
                        </h2>
                    </div>
                    <p
                        onClick={() => navigate('/chroniques')}
                        className="wlink"
                        style={{
                            background: 'none', border: 'none', padding: 0,
                            fontFamily: UI_FONT, fontWeight: 300,
                            fontSize: 'clamp(30px, 6vw, 52px)', letterSpacing: '-0.01em',
                            lineHeight: 1, color,
                        }}

                    >
                        {homeT.viewAll}
                    </p>
                </div>

                {albumsLoading ? (
                    <p style={{ fontFamily: 'Helvetica Neue, sans-serif', fontWeight: 500, fontSize: '13px', color: 'rgba(244,239,228,0.35)', marginTop: '48px' }}>{homeT.loading}</p>
                ) : recentAlbums.length === 0 ? (
                    <p style={{ fontFamily: 'Helvetica Neue, sans-serif', fontWeight: 500, fontSize: '13px', color: 'rgba(244,239,228,0.35)', marginTop: '48px' }}>{homeT.empty}</p>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'clamp(24px, 2.4vw, 40px)', marginTop: '56px', width: '100%' }}>
                        {recentAlbums.map(album => (
                            <div
                                key={album.folder}
                                className="home-chronicle-card"
                                onClick={() => navigate(`/region/${album.region}/${album.folder}`)}
                            >
                                <div className="home-chronicle-cover" style={{ aspectRatio: '1 / 1', overflow: 'hidden', background: '#1c1f24' }}>
                                    {album.cover_url ? (
                                        <img src={album.cover_url} alt={album.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                    ) : (
                                        <div style={{ width: '100%', height: '100%', background: '#1c1f24' }} />
                                    )}
                                </div>
                                <p style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontWeight: 500, fontSize: '14.5px', color: '#f4efe4', margin: '16px 0 0' }}>
                                    {album.title}
                                </p>
                                <p style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontWeight: 500, fontSize: '11px', color: 'rgba(244,239,228,0.4)', margin: '5px 0 0' }}>
                                    {album.location || album.region}
                                    {album.date && ` · ${new Date(album.date).toLocaleDateString('fr-CA', { year: 'numeric', month: 'long', day: 'numeric' })}`}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    if (campaignLoading) {
        return <LoadingScreen variant="dark" />;
    }

    if (!campaign) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#0e1013' }}>
                <Navbar variant="dark" />
                <div style={{ padding: '80px 24px', textAlign: 'center' }}>
                    <p style={{ color: 'rgba(244,239,228,0.4)', fontFamily: 'Helvetica Neue, sans-serif', fontSize: '13px' }}>
                        Aucune campagne active. Va dans l'admin → Campagnes → Activer.
                    </p>
                </div>
                {chroniclesSection}
                <Footer />
            </div>
        );
    }

    return (
        <div style={{ position: 'relative', minHeight: '100vh', fontFamily: 'Helvetica Neue, Arial, sans-serif' }}>

            {popupOpen && popupData && (
                <>
                    <div onClick={() => setPopupOpen(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 999 }} />
                    <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1000, backgroundColor: '#fff', border: '1px solid #e5e5e0', overflow: 'hidden', width: 'min(380px, calc(100vw - 48px))' }}>
                        <button onClick={() => setPopupOpen(false)} className="wlink" style={{ position: 'absolute', top: '12px', right: '12px', background: 'none', border: 'none', width: '24px', height: '24px', cursor: 'pointer', fontSize: '14px', fontWeight: 700, color: '#111', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                        {popupData.image_url && (
                            <img src={popupData.image_url} alt={popupData.title} style={{ width: '100%', height: 'auto', display: 'block' }} />
                        )}
                        {popupData.title && (
                            <div style={{ padding: '18px 20px 20px', textAlign: 'center' }}>
                                <p style={{ fontFamily: 'Helvetica Neue, sans-serif', fontWeight: '500', fontSize: '15px', color: '#111', margin: '0 0 14px', lineHeight: '1.4' }}>{popupData.title}</p>
                                {popupData.link_url && (
                                    <button onClick={() => { setPopupOpen(false); navigate(popupData.link_url); }} className="wlink" style={{ padding: 0, background: 'none', color: '#111', border: 'none', fontSize: '13px', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.03em', fontFamily: 'Helvetica Neue, sans-serif' }}>View →</button>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* HERO — la photo de campagne ne couvre plus que ce premier écran ;
                les sections suivantes défilent normalement par-dessous. */}
            <div style={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
                    <img
                        src={campaign.image_url}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(14,16,19,0.55) 0%, rgba(14,16,19,0.35) 35%, rgba(14,16,19,0.62) 72%, rgba(14,16,19,0.94) 100%)',
                    }} />
                </div>

                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

                    <Navbar variant="dark" />

                    {announcement && (
                        <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(10px)', padding: '32px 20px' }}>
                            <button onClick={() => setAnnouncement(null)} style={{ position: 'absolute', top: '22px', right: '26px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.55)', fontSize: '20px', cursor: 'pointer', padding: '8px', zIndex: 1 }}>✕</button>

                            {/* La "feuille" — un vrai document officiel, pas un panneau promo */}
                            <div style={{
                                background: '#fff', color: '#0e1013', width: '100%', maxWidth: '640px',
                                maxHeight: '90vh', overflowY: 'auto', border: '1px solid #d8d5cc',
                                padding: 'clamp(28px, 5vw, 64px) clamp(24px, 6vw, 60px)', fontFamily: 'Helvetica Neue, Arial, sans-serif',
                                boxSizing: 'border-box',
                            }}>
                                {/* En-tête — logo à gauche, lieu et date à droite comme un vrai papier à en-tête */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', gap: '16px' }}>
                                    <img src="/winston-logo-word.svg" alt="winston.ie" style={{ height: '20px', width: 'auto' }} />
                                    <p style={{ fontSize: '11.5px', fontWeight: 500, color: '#6b6459', margin: 0, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        {announcement.location ? `${announcement.location}, le ` : 'Le '}{formatAnnouncementDate(announcement)}
                                    </p>
                                </div>
                                <p style={{ fontSize: '10px', fontWeight: 500, color: '#a19c8f', margin: '0 0 24px', letterSpacing: '0.02em' }}>
                                    a Longden Collective label
                                </p>

                                <div style={{ width: '100%', height: '1px', background: '#0e1013', marginBottom: '24px' }} />

                                {announcement.recipient && (
                                    <p style={{ fontSize: '13px', fontWeight: 500, color: '#3a3632', margin: '0 0 20px' }}>
                                        À l'attention de : <strong style={{ fontWeight: 700 }}>{announcement.recipient}</strong>
                                    </p>
                                )}

                                <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6459', margin: '0 0 16px' }}>
                                    {typeLabels[announcement.type] || announcement.type}
                                </p>

                                <p style={{ fontSize: '15px', fontWeight: 700, color: '#0e1013', margin: '0 0 28px', lineHeight: 1.4 }}>
                                    Objet : {announcement.title}
                                </p>

                                {announcement.greeting && (
                                    <p style={{ fontSize: '14px', fontWeight: 500, color: '#0e1013', margin: '0 0 18px' }}>
                                        {announcement.greeting}
                                    </p>
                                )}

                                {announcement.body && (
                                    <p style={{
                                        fontSize: '14px', color: '#3a3632', fontWeight: 500, lineHeight: 1.85,
                                        margin: '0 0 32px', textAlign: 'left', whiteSpace: 'pre-wrap',
                                    }}>
                                        {announcement.body}
                                    </p>
                                )}

                                {announcement.pdf_url && (
                                    <div style={{ marginBottom: '32px', overflow: 'hidden', border: '1px solid #e5e2d9' }}>
                                        <iframe src={announcement.pdf_url} style={{ width: '100%', height: '340px', border: 'none', display: 'block', background: 'white' }} title="Document officiel" />
                                    </div>
                                )}

                                {(announcement.closing || announcement.signature_name || announcement.signature_title) && (
                                    <div style={{ textAlign: 'right', marginBottom: '36px' }}>
                                        {announcement.closing && (
                                            <p style={{ fontSize: '14px', fontWeight: 500, color: '#0e1013', margin: '0 0 20px' }}>{announcement.closing}</p>
                                        )}
                                        {announcement.signature_name && (
                                            <p style={{ fontSize: '14px', fontWeight: 700, color: '#0e1013', margin: '0 0 2px' }}>{announcement.signature_name}</p>
                                        )}
                                        {announcement.signature_title && (
                                            <p style={{ fontSize: '12px', fontWeight: 500, color: '#6b6459', margin: 0 }}>{announcement.signature_title}</p>
                                        )}
                                    </div>
                                )}

                                <div style={{ width: '100%', height: '1px', background: '#e5e2d9', marginBottom: '20px' }} />

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                                    <p style={{ fontSize: '10.5px', fontWeight: 500, color: '#a19c8f', margin: 0, letterSpacing: '0.03em' }}>
                                        winston.ie — communiqué officiel
                                    </p>
                                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                        {announcement.link_url && (
                                            <button onClick={() => { setAnnouncement(null); navigate(announcement.link_url); }} className="wlink" style={{ padding: 0, background: 'none', color: '#0e1013', border: 'none', fontSize: '12px', cursor: 'pointer', letterSpacing: '0.04em', fontFamily: 'Helvetica Neue, sans-serif', fontWeight: '700', textTransform: 'uppercase' }}>
                                                Voir →
                                            </button>
                                        )}
                                        {announcement.pdf_url && (
                                            <a href={announcement.pdf_url} target="_blank" rel="noreferrer" className="wlink" style={{ padding: 0, background: 'none', color: '#6b6459', border: 'none', fontSize: '12px', letterSpacing: '0.04em', fontFamily: 'Helvetica Neue, sans-serif', fontWeight: 500, textDecoration: 'none', textTransform: 'uppercase' }}>
                                                PDF
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Contenu du hero — jusqu'ici la campagne active n'était affichée
                        nulle part sur Home ; on y met enfin sa tagline et son crédit. */}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', padding: '40px 24px' }}>
                        <p style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontWeight: 700, fontSize: '10px', letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(244,239,228,0.45)', margin: '0 0 22px' }}>
                            {homeT.campaign(campaign.year)}
                        </p>
                        {campaign.tagline && (
                            <p style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontWeight: 700, fontSize: 'clamp(28px, 4.5vw, 48px)', letterSpacing: '-0.01em', color: '#f4efe4', lineHeight: 1.2, maxWidth: '760px', margin: 0 }}>
                                {campaign.tagline}
                            </p>
                        )}
                        {campaign.tagline_vi && (
                            <p style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontWeight: 500, fontSize: '12px', color: 'rgba(244,239,228,0.4)', marginTop: '16px' }}>
                                {campaign.tagline_vi}
                            </p>
                        )}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '16px', padding: '0 28px 40px' }}>
                        <div>
                            {campaign.photographer_name && (
                                <p
                                    onClick={() => campaign.photographer_username && navigate(`/creator/${campaign.photographer_username}`)}
                                    className={campaign.photographer_username ? 'wlink' : ''}
                                    style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontWeight: 500, fontSize: '11.5px', color: 'rgba(244,239,228,0.55)', cursor: campaign.photographer_username ? 'pointer' : 'default', margin: 0 }}
                                >
                                    {homeT.by} {campaign.photographer_name}
                                </p>
                            )}
                            {campaign.location && (
                                <p style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontWeight: 500, fontSize: '10px', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(244,239,228,0.35)', margin: '4px 0 0' }}>
                                    {campaign.location}
                                </p>
                            )}
                        </div>
                        <button
                            onClick={() => navigate(`/campaign/${campaign.year}`)}
                            className="wlink"
                            style={{
                                background: 'none', border: 'none', padding: 0,
                                fontFamily: UI_FONT, fontWeight: 300,
                                fontSize: 'clamp(30px, 6vw, 52px)', letterSpacing: '-0.01em',
                                lineHeight: 1, color,
                            }}

                        >
                            {homeT.viewCampaign}
                        </button>
                    </div>
                </div>
            </div>

            {chroniclesSection}

            <Footer />
        </div>
    );
}

export default Home;